/**
 * What a discover card's pay line means, as a rule rather than as three
 * separate `Math.min`/`Math.max` expressions.
 *
 * ── Why it is not left inline in the adapter ────────────────────────────────
 * "A card's pay is the spread of its OPEN tasks, and the feed ranks by the end
 * of that spread the sort names" is domain reasoning, not data access, and
 * `SupabaseBountyService` derived it in three places at once: once when
 * mapping a quest row to a card, once when re-pricing a card after the swipe
 * exclusion drops tasks from it, and once when re-ordering the page so the
 * ranking agrees with the figures the cards show. Three copies of one rule is
 * how the mapper and the re-pricer end up disagreeing about whether an
 * assigned task counts — the exact class of misquote the range was added to
 * remove. One module decides; the adapter fetches rows and calls it.
 */

export interface PayRange {
  /** The cheapest job on offer — the floor a card leads with. */
  budgetUSD: number;
  /** The dearest. Equal to the floor when every open task pays the same. */
  budgetMaxUSD: number;
}

/**
 * The range across a set of task budgets, or null when there are none.
 *
 * Null rather than a zero range on purpose: "no open task has a recorded pay"
 * is not "this work is unpaid", and the callers differ on what to do about it
 * — the mapper falls back to the row it does have, the swipe re-pricer leaves
 * the card's existing figures alone. Collapsing the empty case here would hand
 * both of them `Infinity`/`-Infinity`, which is what a bare
 * `Math.min(...[])` yields.
 */
export function payRangeFromBudgets(budgets: number[]): PayRange | null {
  if (budgets.length === 0) return null;
  return { budgetUSD: Math.min(...budgets), budgetMaxUSD: Math.max(...budgets) };
}

/**
 * Rank cards by the end of their range that the chosen sort actually names.
 *
 * Asymmetric ON PURPOSE, and it mirrors the server-side order this corrects:
 * "cheapest first" asks what the cheapest job on a card pays (the floor),
 * "best paid first" asks what the best-paid one pays (the ceiling). Ranking
 * both ends by the same bound would put a 0–300 quest below a flat 100 under
 * `budget_desc`, on a card that visibly advertises 300.
 *
 * Returns a new array and relies on `Array.prototype.sort` being stable, so
 * cards that tie keep the order the server gave them.
 */
export function sortByPayRange<T extends { budgetUSD: number; budgetMaxUSD?: number }>(
  cards: T[],
  direction: "asc" | "desc",
): T[] {
  return [...cards].sort((a, b) =>
    direction === "asc"
      ? a.budgetUSD - b.budgetUSD
      : (b.budgetMaxUSD ?? b.budgetUSD) - (a.budgetMaxUSD ?? a.budgetUSD),
  );
}
